import { Injectable } from '@angular/core';
import { BaseApiService } from '@ep/shared';
import { OrderPayment, OrderPaymentsResponse } from '@app/resources/services/order.service';

export interface ConsumerLoyaltyProgramResponse {
  consumerLoyaltyProgramId: number;
  consumerId: number;
  merchantId: number;
  name: string;
  averageSpend: number;
  lifetimeSpend: number;
  totalVisits: number;
  rewardBalance: number;
  pointsBalance: number;
  lifetimePointsBalance: number;
  lifetimeRewardsRedeemed: number;
}

export interface SpendWindowResponse {
  days: number | null;
  totalSpend: number;
  visits: number;
  averageSpend: number;
  averageDaysBetweenVisits: number;
  rewardsEarned: number;
  rewardsRedeemed: number;
  pointsEarned: number;
}

export interface SpendWindowsResponse {
  $values: SpendWindowResponse[];
}

export interface ConsumerRelationshipResponse {
  consumerRelationshipId: number;
  consumerId: number;
  merchantId: number;
  totalOrderCount: number;
  totalSpendAmount: number;
  assignDateTime: number;
  lastSeenDateTime: number;
  tenureDays: number;
  spendWindows: SpendWindowsResponse;
}

export interface ConsumerResponse {
  consumerId: number;
  nameFirst: string;
  nameLast: string;
  phoneNumber: string;
  emailAddress: string;
  consumerRelationship: ConsumerRelationshipResponse | null;
  loyaltyProgram: ConsumerLoyaltyProgramResponse | null;
  orderPayments: OrderPaymentsResponse;
}

export interface ConsumersResponse {
  $values: ConsumerResponse[];
}

export interface ConsumerLoyaltyProgram {
  ConsumerLoyaltyProgramId: number;
  ConsumerId: number;
  MerchantId: number;
  Name: string;
  AverageSpend: number;
  LifetimeSpend: number;
  TotalVisits: number;
  RewardBalance: number;
  PointsBalance: number;
  LifetimePointsBalance: number;
  LifetimeRewardsRedeemed: number;
}

/** Metrics over a rolling window of days, or over the member's lifetime when Days is null. */
export interface ConsumerSpendWindow {
  Days: number | null;
  TotalSpend: number;
  Visits: number;
  AverageSpend: number;
  AverageDaysBetweenVisits: number;
  RewardsEarned: number;
  RewardsRedeemed: number;
  PointsEarned: number;
}

export interface ConsumerRelationship {
  ConsumerRelationshipId: number;
  ConsumerId: number;
  MerchantId: number;
  TotalOrderCount: number;
  TotalSpendAmount: number;
  AssignDateTime: number;
  LastSeenDateTime: number;
  TenureDays: number;
  /** Lifetime first, then only the rolling windows the member's tenure covers. */
  SpendWindows: ConsumerSpendWindow[];
}

export interface Consumer {
  ConsumerId: number;
  NameFirst: string;
  NameLast: string;
  PhoneNumber?: string;
  EmailAddress?: string;
  ConsumerRelationship: ConsumerRelationship | null;
  LoyaltyProgram?: ConsumerLoyaltyProgram | null;
  OrderPayments?: OrderPayment[];
}

@Injectable({
  providedIn: 'root',
})
export class ConsumerService extends BaseApiService {
  private readonly consumerEndpoint = '/v2/Consumers';

  findAll() {
    return this.get<ConsumersResponse>(`${this.consumerEndpoint}`);
  }

  findUnique(consumerId: number) {
    return this.get<ConsumerResponse>(`${this.consumerEndpoint}/${consumerId}`);
  }

  findNonMembers() {
    return this.get<ConsumersResponse>(`${this.consumerEndpoint}/get-non-members`);
  }

  findNonMember(consumerId: number) {
    return this.get<ConsumerResponse>(`${this.consumerEndpoint}/get-non-member-by-id/${consumerId}`);
  }
}
